/** Client-side helpers for the rate card screens. The server's numbers are the official ones. */

export type Incentive = { dailyTargetInches?: string; cutoffPercent?: string; bonusPercent?: string } | null;

export type RateLine = {
  workTypeId: string;
  workTypeCode: string;
  workTypeName: string;
  weaverRatePerInch?: string | null;
  sourceExpression?: string | null;
  sourceUnit?: "INCH" | "GIRAH" | null;
  aloneAllowancePerInch?: string | null;
  /** Extra ₹ per inch paid only on entries marked ladka sool. */
  ladkaSoolPerInch?: string | null;
  incentive?: Incentive;
};

export type RateVersion = {
  id: string;
  versionNo: number;
  status: "DRAFT" | "APPROVED" | "DISCARDED";
  effectiveFrom: string;
  effectiveTo?: string | null;
  note?: string | null;
  lines: RateLine[];
  approvedByName?: string | null;
  approvedAt?: string | null;
  createdByName?: string | null;
};

/** One editable row in the draft grid. Strings, exactly as typed. */
export type DraftRow = {
  workTypeId: string;
  code: string;
  name: string;
  rate: string;
  rateUnit: "INCH" | "GIRAH";
  alone: string;
  /** Ladka sool extra, in the same unit as the rate. */
  sool: string;
  target: string;
  cutoff: string;
  bonus: string;
};

const PER = /^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/;
const NUM = /^\d+(?:\.\d+)?$/;

/** Rupees per inch for what was typed, or null if it can't be read. Mirrors RateMath on the server. */
export const perInch = (raw: string, unit: "INCH" | "GIRAH", inchesPerGirah: number): number | null => {
  const v = (raw || "").replace(/[₹,\s]/g, "");
  if (!v) return null;
  let perUnit: number;
  const m = v.match(PER);
  if (m) {
    const qty = Number(m[2]);
    if (!qty) return null;
    perUnit = Number(m[1]) / qty;
  } else if (NUM.test(v)) {
    perUnit = Number(v);
  } else {
    return null;
  }
  return unit === "GIRAH" ? perUnit / inchesPerGirah : perUnit;
};

/**
 * One money format everywhere: paise only when there are any (₹700, ₹3.33), minus before the sign (−₹200).
 * `digits` is kept for older callers but no longer rounds, so a row and its total always agree.
 */
export const rupees = (n: number | string | null | undefined, digits = 2): string => {
  void digits;
  if (n === null || n === undefined || n === "") return "—";
  const x = typeof n === "string" ? Number(n) : n;
  if (!isFinite(x)) return "—";
  const paise = Math.round(Math.abs(x) * 100) % 100 !== 0;
  const body = Math.abs(x).toLocaleString("en-IN", { minimumFractionDigits: paise ? 2 : 0, maximumFractionDigits: 2 });
  return `${x < 0 && body !== "0" ? "−" : ""}₹${body}`;
};

export const fmtDay = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

/** A pay sheet label as the server sends it, "2026-10-26 to 2026-11-01" → "26 Oct 2026 – 1 Nov 2026"; other text as is. */
export const fmtRange = (label?: string | null): string => {
  const m = /^(\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})$/.exec((label || "").trim());
  return m ? `${fmtDay(m[1])} – ${fmtDay(m[2])}` : label || "";
};

export const todayIso = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** "2026-10-18", -1 → "2026-10-17" (local calendar days). */
export const shiftDay = (iso: string, days: number): string => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const versionLabel = (v: RateVersion): string => {
  if (v.status === "DRAFT") return `v${v.versionNo} · draft`;
  if (v.status === "DISCARDED") return `v${v.versionNo} · discarded`;
  return `v${v.versionNo} · ${fmtDay(v.effectiveFrom)}${v.effectiveTo ? ` – ${fmtDay(v.effectiveTo)}` : " onwards"}`;
};

/** All work types as rows, filled from a version's lines. Rates typed per girah come back as typed. */
const CATEGORY_ORDER = ["SAREE", "LEHENGA", "TAANI", "LOOM_PREP"];

/** Saree parts first, then the rest; S_2 before S_10. */
export const sortWorkTypes = <T extends { code: string; category?: string }>(list: T[]): T[] => {
  const cat = (w: T) => {
    const i = CATEGORY_ORDER.indexOf(String(w.category || ""));
    return i < 0 ? CATEGORY_ORDER.length : i;
  };
  return [...list].sort(
    (a, b) => cat(a) - cat(b) || a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: "base" })
  );
};

/** A ₹-per-inch value shown in the line's unit (per girah = per inch × inches per girah), without float noise. */
export const inUnit = (perInchValue: string | null | undefined, unit: "INCH" | "GIRAH", inchesPerGirah: number): string => {
  if (!perInchValue) return "";
  if (unit !== "GIRAH") return perInchValue;
  const n = Number(perInchValue) * inchesPerGirah;
  return isFinite(n) ? String(Math.round(n * 10000) / 10000) : "";
};

export const toDraftRows = (workTypes: any[], lines: RateLine[], inchesPerGirah = 2.6875): DraftRow[] => {
  const byId = new Map(lines.map((l) => [l.workTypeId, l]));
  return sortWorkTypes(workTypes)
    .filter((w) => w.active !== false || byId.has(w.id))
    .map((w) => {
      const l = byId.get(w.id);
      const rateUnit = (l?.sourceUnit as "INCH" | "GIRAH") || "INCH";
      return {
        workTypeId: w.id,
        code: w.code,
        name: w.name,
        rate: l?.sourceExpression || l?.weaverRatePerInch || "",
        rateUnit,
        alone: l?.aloneAllowancePerInch || "",
        sool: inUnit(l?.ladkaSoolPerInch, rateUnit, inchesPerGirah),
        target: l?.incentive?.dailyTargetInches || "",
        cutoff: l?.incentive?.cutoffPercent || "",
        bonus: l?.incentive?.bonusPercent || "",
      };
    });
};

export const toLineInputs = (rows: DraftRow[]) =>
  rows
    .filter((r) => r.rate || r.alone || r.sool || r.target || r.cutoff || r.bonus)
    .map((r) => ({
      workTypeId: r.workTypeId,
      rate: r.rate,
      rateUnit: r.rateUnit,
      aloneAllowance: r.alone,
      aloneUnit: "INCH",
      ladkaSool: r.sool,
      dailyTargetInches: r.target,
      cutoffPercent: r.cutoff,
      bonusPercent: r.bonus,
    }));

/** Which fields of a row differ from the current approved rates, to highlight them. */
export const changedFields = (row: DraftRow, base: RateLine | undefined, inchesPerGirah: number): Set<string> => {
  const out = new Set<string>();
  const same = (a: number | null, b: string | null | undefined) =>
    (a === null && !b) || (a !== null && !!b && Math.abs(a - Number(b)) < 0.00005);
  if (!same(perInch(row.rate, row.rateUnit, inchesPerGirah), base?.weaverRatePerInch)) out.add("rate");
  if (!same(perInch(row.alone, "INCH", inchesPerGirah), base?.aloneAllowancePerInch)) out.add("alone");
  if (!same(perInch(row.sool, row.rateUnit, inchesPerGirah), base?.ladkaSoolPerInch)) out.add("sool");
  const num = (s: string) => (s ? Number(s) : null);
  if (!same(num(row.target), base?.incentive?.dailyTargetInches)) out.add("target");
  if (!same(num(row.cutoff), base?.incentive?.cutoffPercent)) out.add("cutoff");
  if (!same(num(row.bonus), base?.incentive?.bonusPercent)) out.add("bonus");
  return out;
};

/**
 * A part's name in the screen's language: the server's Hindi name (nameHi) in Hindi mode, else the
 * English name through the Hindi dictionary (t), else the code.
 */
export const partName = (w: { name?: string; nameHi?: string | null; code?: string } | null | undefined, lang: string, t: (s: string) => string): string => {
  if (!w) return "";
  if (lang === "hi" && w.nameHi) return w.nameHi;
  return w.name ? t(w.name) : w.code || "";
};

/** Version history for a design, oldest first, discarded drafts left out: "v1 · 1 Jan 2020 – 31 Dec 2020". */
export const versionHistory = <V extends Pick<RateVersion, "status" | "effectiveFrom" | "versionNo"> & { effectiveTo?: string | null }>(versions: V[]): V[] =>
  versions
    .filter((v) => v.status !== "DISCARDED")
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom) || a.versionNo - b.versionNo);
